import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = "https://readdy.ai/api/form/d9pehdti2vhsdh2d32mg";

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormError('');
      return;
    }

    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt' || !value || String(value).trim() === '') {
        urlEncoded.append(key, value as string);
      }
    });

    fetch(formUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlEncoded.toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: any = {};
        try { parsed = JSON.parse(text); } catch { /* raw response */ }

        if (res.ok && parsed?.code === 'OK') {
          setFormStatus('success');
          setFormError('');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后重试';
          if (msg.includes('spam') || msg.includes('form data is spam')) {
            setFormStatus('error');
            setFormError(msg);
          } else {
            setFormStatus('error');
            setFormError(msg);
          }
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormError('网络错误，请稍后重试');
      });
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4" id="contact-form">
      {formStatus === 'success' && (
        <div className="p-4 bg-accent-50 text-accent-800 rounded-lg text-sm font-medium">
          感谢您的留言！我们会尽快回复您。
        </div>
      )}
      {formStatus === 'error' && formError && (
        <div className="p-4 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium">
          {formError}
        </div>
      )}
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
        <input
          type="text"
          id="contact-name"
          name="name"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
        <input
          type="email"
          id="contact-email"
          name="email"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的邮箱地址"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          required
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入留言主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容（不超过500字）"
        />
      </div>
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="honeypot-field"
      />
      <button
        type="submit"
        className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
      >
        提交留言
      </button>
    </form>
  );
}

const testimonials = [
  {
    name: "小美",
    role: "韩漫爱好者",
    content: "韩漫漫画网是我每天必逛的网站！推荐的漫画质量都很高，排行榜也很靠谱，帮我发现了好多冷门佳作，太感谢了！",
    rating: 5,
  },
  {
    name: "阿强",
    role: "漫画读者",
    content: "分类做得特别细致，想找什么类型的漫画都很方便。每篇评测都写得很用心，看完评测再决定要不要追，节省了好多时间。",
    rating: 5,
  },
  {
    name: "小雨",
    role: "Webtoon粉丝",
    content: "作为一个韩漫老读者，这个网站的推荐品味真的很对我胃口。更新也很及时，新出的好漫画第一时间就能看到推荐。",
    rating: 5,
  },
  {
    name: "大雄",
    role: "二次元爱好者",
    content: "之前找韩漫总要翻来翻去，现在直接看韩漫漫画网的排行榜就够了。界面也很清爽，用起来很舒服。",
    rating: 4,
  },
];

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="关于我们 - 品牌介绍"
        description="韩漫漫画网致力于为中文读者提供最优质的韩国漫画推荐服务。了解我们的品牌故事、用户口碑与联系方式。"
        keywords="韩漫漫画网关于我们,韩国漫画推荐平台,韩漫评测,联系我们"
        canonicalUrl="https://www.hanmancomic.com/about"
      />
      <SchemaOrg
        pageType="AboutPage"
        breadcrumbs={[
          { name: '首页', url: '/' },
          { name: '关于我们', url: '/about' },
        ]}
      />

      <Navbar />

      <main className="min-h-screen">
        <div className="px-4 md:px-6 py-6 md:py-8 max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: '关于我们', url: '/about' },
            ]}
          />

          <section className="mb-10 md:mb-14">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 md:mb-6">
              关于韩漫漫画网
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h2 className="font-heading text-lg md:text-2xl font-semibold text-foreground-950 mb-3 md:mb-4">
                  我们的品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  韩漫漫画网诞生于对韩国漫画的热爱。我们的创始团队是一群资深韩漫读者，在多年的阅读经历中深刻感受到：优秀的韩国漫画浩如烟海，但中文读者缺少一个专业、系统的推荐平台。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  于是，韩漫漫画网应运而生。我们从零开始，精心筛选每一部韩国漫画作品，为中文读者提供最新热门韩国漫画推荐和不掺水分的无删减韩国漫画排行榜。我们的目标是让每一位热爱漫画的读者都能在这里找到属于自己的那一部佳作。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  自成立以来，韩漫漫画网始终坚持独立、客观的评测态度，不随波逐流，不做商业推广。我们相信，好的作品自会发光，而我们只是帮助那束光被更多人看到。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20style%20illustration%2C%20cozy%20reading%20corner%20with%20bookshelf%20and%20warm%20lighting%2C%20comfortable%20atmosphere%2C%20Korean%20digital%20art%20style%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20inviting%20and%20aesthetic%20interior%20scene%20with%20books%20and%20plants&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="韩漫漫画网品牌形象"
                  title="韩漫漫画网 - 品牌介绍"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          <section className="mb-10 md:mb-14 py-8 md:py-10 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-6 md:mb-8">
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  听听我们的读者怎么说
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
                {testimonials.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 md:p-5 bg-background-50 rounded-lg border border-background-200/70"
                  >
                    <div className="flex items-center gap-0.5 text-accent-500 mb-2 md:mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < item.rating ? 'ri-star-fill' : 'ri-star-line text-foreground-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3 md:mb-4">
                      "{item.content}"
                    </p>
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-sm">
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                        <p className="text-xs text-foreground-500">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h2 className="font-heading text-lg md:text-2xl font-semibold text-foreground-950 mb-3 md:mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 md:mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们期待听到您的声音。
                </p>
                <div className="space-y-3 md:space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">邮箱地址</p>
                      <p className="text-sm text-foreground-600">contact@hanmancomic.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-global-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">官方网站</p>
                      <p className="text-sm text-foreground-600">www.hanmancomic.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="font-heading text-lg md:text-2xl font-semibold text-foreground-950 mb-3 md:mb-4">
                  在线留言
                </h2>
                <p className="text-sm text-foreground-600 mb-4">
                  您也可以直接通过以下表单给我们留言，我们会尽快回复您。
                </p>
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}